import { toast, ToastOptions } from 'react-hot-toast';
import { createHash } from 'crypto';

/**
 * Displays a toast notification based on the provided type.
 *
 * @param {string} message - The message to display in the toast.
 * @param {"success" | "error" | "info" | "loading"} type - The type of toast notification.
 * @param {boolean} [showOnlyOnce=true] - If true, prevents duplicate messages by using a hash-based ID.
 *
 * Supported types:
 * - "success": Displays a success toast.
 * - "error": Displays an error toast.
 * - "info": Displays a neutral info toast.
 * - "loading": Displays a loading toast.
 *
 * Example Usage:
 * ```tsx
 * showToastr("Data saved successfully!", "success");
 * ```
 */
export const lomavisShowToastr = (
  message: string,
  type: 'success' | 'error' | 'info' | 'loading',
  showOnlyOnce: boolean = true
) => {
  // Generate a hash key based on the message for uniqueness
  const messageHash = createHash('md5').update(message).digest('hex');
  const toastId = showOnlyOnce ? `toastr-${messageHash}` : undefined;

  // Define toast options with proper typing
  const options: ToastOptions = {
    duration: 4000,
    position: 'top-right',
    id: toastId
  };

  // Show the appropriate toast type
  switch (type) {
    case 'success':
      toast.success(message, options);
      break;
    case 'error':
      toast.error(message, options);
      break;
    case 'info':
      toast(message, options);
      break;
    case 'loading':
      toast.loading(message, options);
      break;
    default:
      console.warn('Invalid toast type provided');
  }
};
